// packages
import { createRaceFeed } from '../stores.js';
import {
  renderSvelteComponent,
  findElementByIdInShadowDom,
  findElementInShadowDom,
} from './render.js';

//components
import RaceTable from '../components/RaceTable.svelte';

const raceComponentMap = {
  result: { component: RaceTable, extraProps: {} },
  resultWithHeadshot: {
    component: RaceTable,
    extraProps: { face: true, crosswalk: '' },
  },
  proposition: { component: RaceTable, extraProps: {} },
  local_result_ap: {
    component: RaceTable,
    extraProps: {
      shouldSortCandidates: true,
      showCaption: false,
      captionNote: '',
    },
  },
  local_result: {
    component: RaceTable,
    extraProps: {
      shouldSortCandidates: true,
      showCaption: false,
      captionNote: '',
      hidePrecincts: false,
      componentRaceType: 'local_result',
    },
  },
};

/**
 * Initializes and renders race components within a given container.
 * @param {HTMLElement | Document} container - The DOM node to search within.
 * @param {any} feed - The raw feed data used to generate race feeds.
 * @param {Record<string, any>} [mountProps] - Additional props passed from the mount call.
 */
export function findAndRenderRaceResultElements(
  container,
  feed,
  mountProps = {}
) {
  // if there are several .race-results (ex: multiple races)
  if (container.querySelectorAll('.race-result').length) {
    let elements = container.querySelectorAll('.race-result');
    elements.forEach((element) => {
      const el = /** @type {HTMLElement} */ (element);
      let raceType = el.dataset.type;
      let config = raceType
        ? raceComponentMap[
            /** @type {keyof typeof raceComponentMap} */ (raceType)
          ]
        : undefined;

      // skip elements that don't match
      if (!config) return;

      let raceFeed = /** @type {import('../types').ReadableRace} */ (
        createRaceFeed(feed, el.id)
      );

      renderSvelteComponent(
        config.component,
        /** @type {HTMLElement} */ (element),
        {
          feed: raceFeed,
          ...mountProps,
          ...config.extraProps,
        }
      );
    });
  } else {
    // if it's just one container that we're passing through, use the container info
    const el = /** @type {HTMLElement} */ (container);
    let raceType = el.dataset.type;
    let config = raceType
      ? raceComponentMap[
          /** @type {keyof typeof raceComponentMap} */ (raceType)
        ]
      : undefined;

    if (!config) return;

    let raceFeed = /** @type {import('../types').ReadableRace} */ (
      createRaceFeed(feed, el.id)
    );

    renderSvelteComponent(config.component, /** @type {HTMLElement} */ (el), {
      feed: raceFeed,
      ...mountProps,
      ...config.extraProps,
    });
  }
}

/**
 * Filters items based on dropdown selection
 * @param {string} value - The selected dropdown value
 * @param {string} section - 'cities' or 'school-boards'
 */
export function filterDropdown(value, section) {
  let isCities = section === 'cities';

  // we only have dropdowns so far for cities and school boards, so
  // this will have to be rewritten if we add dropdowns for other multiple
  // races element
  let itemClass = isCities
    ? '.cities-race-result-item'
    : '.schools-race-result-item';
  let containerId = isCities
    ? 'race-embed-container--cities'
    : 'race-embed-container--schools';

  let resultsEl = findElementByIdInShadowDom(containerId);
  if (!resultsEl) return;

  let items = resultsEl.querySelectorAll(itemClass);

  let toggleVisibility = (
    /** @type {Element} */ element,
    /** @type {boolean} */ isVisible
  ) => {
    element.classList.toggle('hidden', !isVisible);
    element.setAttribute('aria-hidden', (!isVisible).toString());
  };

  items.forEach((item) => {
    // get the parent element. If it doesn't have one, exit the function
    let parent = item.parentElement;
    if (!parent) return;

    let shouldShow = value === 'show_all' || item.classList.contains(value);
    let isLaEdgeCase = value === 'item-los-angeles' && isCities;

    // Toggle the child item
    toggleVisibility(item, shouldShow);

    // Toggle the parent item ONLY if it's 'show_all' or the L.A. case (the la one is in its own container)
    if (value === 'show_all' || isLaEdgeCase) {
      toggleVisibility(parent, shouldShow);
    }
  });
}

/**
 * @param {string} selector
 * @param {string} section
 */
export function setDropdownEventListener(selector, section) {
  let setupListener = (
    /** @type {string} */ selector,
    /** @type {string} */ section
  ) => {
    let dropdown = findElementInShadowDom(selector);
    dropdown?.addEventListener('change', (event) => {
      filterDropdown(
        /** @type {HTMLSelectElement} */ (event.target).value || '',
        section
      );
    });
  };

  setupListener(selector, section);
}
